import {
    Utils,
    Logger,
    PreloaderInfo,
    Preloader,
    libraryInitialize as neoCommonInitialize,
    PreloaderProviderBase,
} from "neo-common";

neoCommonInitialize();

import { UriHelper, StringUtils } from "neo-common";
import { GameEngine, ClientHost, Communication, Platforms } from "neo-games";
import { Strings } from "../Common/Strings";
import { StageController } from "./Components/StageController";
import { PreloaderProvider } from "./Components/Managers/PreloaderProvider";
import { Languages } from "./Utils/Languages";
import { GameClientHostConsumer } from "./ClientHost/ClientHostConsumer";
import { GameDefinition } from "../Common/GameDefinition";
import { GameConfiguration } from "../Common/GameConfiguration";
import { AppContext } from "../Common/AppContext";
import { GameData } from "./Channels/Common/Datas/GameData";
import { HtmlController } from "./HTMLComponents/HtmlController";
import { GameManager } from "./Managers/GameManager";
import { DataManager } from "./Managers/DataManager";
import { PlatformsManager } from "./Managers/PlatformsManager";
import { FreeRoundBonus } from "./Channels/Common/Bonuses/FreeRoundBonus";
import { Scaling } from "./Utils/Scaling";
import { RetentionPanel } from "./Utils/RetentionPanel";
import { SplashScreen } from "./CustomComponents/SplashScreen";

if (DEBUG) {
    require("./Utils/PerfDebug/PerfDebug");
    require("./Utils/PerfDebug/PerfDebugWindow");
}

var gameApp: GameApp;

export class GameApp extends GameEngine.GameAppBase<GameConfiguration, GameDefinition, GameClientHostConsumer> {
    public static language: string = "en-US";
    public static key: string = "MoneyVaults";

    public static main(): void {
        if (DEBUG) {
            Logger.debugGroup("Application", "start main");
        }

        let urlInfo: Object = UriHelper.parseUrl(window.location.href, true, true);
        if (urlInfo["query"]["lang"] != null) {
            GameApp.language = urlInfo["query"]["lang"];
        }

        RetentionPanel.initialize();
        gameApp = new GameApp();
        gameApp.load();
    }

    public static get gameClient(): Communication.Game.GameClient {
        return gameApp._gameClient;
    }

    public static exit(): Promise<any> {
        Platforms.Platforms.gameClosing();
        if (AppContext.gameConfiguration.ExitUrl != "") {
            Platforms.Platforms.userAction("exit", { url: AppContext.gameConfiguration.ExitUrl });
        }
        return gameApp.exit();
    }

    public static history(): Promise<any> {
        return gameApp.history();
    }

    public static continue(): Promise<any> {
        return gameApp.continue();
    }

    public static closeRealityCheck(): Promise<any> {
        return gameApp.closeRealityCheck();
    }

    protected createGameClient(options: Communication.Game.GameClientOptions): Communication.Game.GameClient {
        let gameClient: Communication.Game.GameClient = new Communication.Game.GameClient(GameApp.key, options);
        FreeRoundBonus.initialize(gameClient);
        return gameClient;
    }

    protected pauseAnimations(): void {
        StageController.pauseAnimations();
    }

    protected resumeAnimations(): void {
        StageController.resumeAnimations();
    }

    protected createPreloaderProvider(): PreloaderProviderBase {
        return new PreloaderProvider();
    }

    protected createGameDefinition(definition: Object): GameDefinition {
        return new GameDefinition(definition);
    }

    protected createGameConfiguration(configuration: Object): GameConfiguration {
        return new GameConfiguration(configuration);
    }

    protected createAppContext(): GameEngine.AppContextBase<GameConfiguration, GameDefinition> {
        return new AppContext(this.gameConfiguration, this.gameDefinition);
    }

    protected createClientHostConsumer(): GameClientHostConsumer {
        return new GameClientHostConsumer(this);
    }

    protected requireStrings(): void {
        require("../Common/Strings");
    }

    protected createStage(quality: string, size: GameEngine.Types.GameQualitySize): void {
        let color = 0x000000;
        StageController.createStage(size.width, size.height, color, "canvas");
    }

    protected initClientHost(): Promise<any> {
        PlatformsManager.create();

        return Promise.resolve();
    }

    protected parseLoadResult(loadResult: GameEngine.Types.AppLoadResult): Object {
        let result: Object = null;

        if (loadResult != null) {
            if (loadResult.status == GameEngine.Types.AppLoadStatus.Failure) {
                if (loadResult.error != null) {
                    result = {
                        message: loadResult.error.message,
                        stack: loadResult.error.stack,
                    };
                }
            }
        }

        return result;
    }

    protected finalizeLoad(): void {
        if (this.loadingSucceed) {
            if (AppContext != null && AppContext.current != null) {
                let volume = AppContext.current.getVolume();
                if (!Utils.isNullOrUndefined(volume)) {
                    if (!Utils.isNullOrUndefined(volume.CurrentStepNo)) {
                        if (DEBUG) {
                            Logger.debugGroup(
                                this.loggerGroup,
                                `Updating sound volume by step ${volume.CurrentStepNo}.`,
                            );
                        }

                        HtmlController.setSound(volume.CurrentStepNo);
                    } else {
                        if (DEBUG) {
                            Logger.debugGroup(
                                this.loggerGroup,
                                `client host does not return valid value for volume 'CurrentStepNo'!`,
                            );
                        }
                    }
                } else {
                    if (DEBUG) {
                        Logger.debugGroup(
                            this.loggerGroup,
                            `client host does not returns volume data, sound volume was not updated.`,
                        );
                    }
                }

                Scaling.resizeRoot();
            }

            // Updating build version (for DEBUG only, for RELEASE an empty string is used)
            let versionBuild = "";
            if (DEBUG) {
                let versionBuildValues: Array<string> = Preloader.getVersionBuild().split("_");
                versionBuild = Utils.valueOrDefault(
                    versionBuildValues && versionBuildValues.length > 0
                        ? versionBuildValues[versionBuildValues.length - 1]
                        : "",
                    "",
                );
            }
            DataManager.updateGameDataFromModule(GameData.DataBuildVersion, versionBuild);
            DataManager.configureGameData();

            if (Platforms.Platforms.canPlay) {
                GameManager.startGame(this._gameClient, this);
                Platforms.Platforms.gameReady();
            } else {
                var loadingScreen = document.getElementById("loading");
                var element: HTMLElement = document.getElementById("progressNumber");

                if (loadingScreen != null) {
                    loadingScreen.classList.add("_error");
                }
                if (element != null) {
                    element.innerHTML = Strings.loaded
                        ? Languages.getRid("ERROR_404")
                        : "Internal error. Please contact support.";
                    GameApp.gameClient.sendPlayerLogError(
                        "ERROR - Loading game",
                        this.parseLoadResult(this._loadResult),
                    );
                }

                SplashScreen.showError();
            }
        } else {
            Platforms.Platforms.gameFailed();
            window.onblur = null;
            window.onfocus = null;
            PIXI.sound.muteAll();

            var loadingScreen = document.getElementById("loading");
            var element: HTMLElement = document.getElementById("progressNumber");

            if (loadingScreen != null) {
                loadingScreen.classList.add("_error");
            }
            if (element != null) {
                element.innerHTML = Strings.loaded
                    ? Languages.getRid("ERROR_404")
                    : "Internal error. Please contact support.";
                GameApp.gameClient.sendPlayerLogError("ERROR - Loading game", this.parseLoadResult(this._loadResult));
            }

            SplashScreen.showError();

            if (DEBUG) {
                var err = "";
                if (!PreloaderInfo.instance.loadSucceed) {
                    err += " Preloader does not load successfully!";
                }

                Logger.errorGroup(this.loggerGroup, err);
            }
        }

        if (DEBUG) {
            PerfDebug.setup(StageController.Stage);
        }
    }

    static openGameRoundHistory(isGUI: boolean = false): void {
        if (isGUI) {
            if (
                !StringUtils.isNullOrEmpty(AppContext.gameConfiguration.GUIHistoryUrl) &&
                AppContext.gameConfiguration.GUIHistoryUrl !== ""
            ) {
                if (
                    AppContext.gameConfiguration.GUIHistoryUrl.toLowerCase() === "userci_historyurl" &&
                    !StringUtils.isNullOrEmpty(AppContext.gameConfiguration.RCIHistoryUrl)
                ) {
                    Platforms.Platforms.gotoHistory({ target: AppContext.gameConfiguration.RCIHistoryUrl });
                    if (Platforms.Platforms.handleOpenHistoryPage) {
                        return;
                    }
                    window.open(AppContext.gameConfiguration.RCIHistoryUrl, "_blank");
                } else if (
                    AppContext.gameConfiguration.GUIHistoryUrl.toLowerCase() === "userci_historyurl" &&
                    StringUtils.isNullOrEmpty(AppContext.gameConfiguration.RCIHistoryUrl)
                ) {
                    GameApp.openGameRoundHistoryHome();
                } else {
                    Platforms.Platforms.gotoHistory({ target: AppContext.gameConfiguration.GUIHistoryUrl });
                    if (Platforms.Platforms.handleOpenHistoryPage) {
                        return;
                    }
                    window.open(AppContext.gameConfiguration.GUIHistoryUrl, "_blank");
                }
            } else {
                GameApp.openGameRoundHistoryHome();
            }
        } else {
            GameApp.openGameRoundHistoryHome();
        }
    }

    static openGameRoundHistoryHome(): void {
        let gameKey: string = UriHelper.getGameKey();
        let urlInfo: Object = UriHelper.parseUrl(window.location.href, true, true);
        let timeZoneOffset: Number = new Date().getTimezoneOffset();

        if (gameKey != null && urlInfo != null && urlInfo["query"]["serverUrl"] && urlInfo["hash"]) {
            let serverUrl: string = urlInfo["query"]["serverUrl"];
            let token: string = urlInfo["hash"].substring(1);
            let lang: string = urlInfo["query"]["lang"] ? urlInfo["query"]["lang"] : GameApp.language;
            const historyUrl =
                serverUrl + "GameRounds/" + GameApp.key + "?culture=" + lang + "&tzo=" + timeZoneOffset + "&" + token;
            Platforms.Platforms.gotoHistory({ target: historyUrl });
            if (Platforms.Platforms.handleOpenHistoryPage) {
                return;
            }
            window.open(historyUrl, "_blank");
        }
    }

    static openGameRoundHistoryFromUrl(): void {
        if (!StringUtils.isNullOrEmpty(AppContext.gameConfiguration.RCIHistoryUrl)) {
            Platforms.Platforms.gotoHistory({ target: AppContext.gameConfiguration.RCIHistoryUrl });
            if (Platforms.Platforms.handleOpenHistoryPage) {
                return;
            }
            window.open(AppContext.gameConfiguration.RCIHistoryUrl, "_blank");
        } else if (!StringUtils.isNullOrEmpty(AppContext.gameConfiguration.HistoryUrl)) {
            Platforms.Platforms.gotoHistory({ target: AppContext.gameConfiguration.HistoryUrl });
            if (Platforms.Platforms.handleOpenHistoryPage) {
                return;
            }
            window.open(AppContext.gameConfiguration.HistoryUrl, "_blank");
        }
    }

    /**
     * Before exist do reguler 'leave game' request against server
     * @returns {Promise<any>}
     */
    protected beforeExit(): Promise<any> {
        return this._gameClient.leaveGame();
    }

    protected handleStringsLoadCompleted(lang: string): void {
        if (HtmlController.RidElements != null && HtmlController.RidElements.length > 0) {
            for (let i: number = 0; i < HtmlController.RidElements.length; i++) {
                HtmlController.RidElements[i].updateText();
            }
        }

        let languageElements: NodeListOf<Element> = document.querySelectorAll("[language]");
        for (let i: number = 0; i < languageElements.length; i++) {
            languageElements[i].setAttribute("language", lang);
        }
    }
}

GameApp.main();



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameApp.ts
 **/